<template>
  <div class="profile-panel">
    <div class="profile-header">
      <div class="avatar" @click="handleAvatarClick">
        <img v-if="userInfo.pendant" :src="userInfo.pendant" class="pendant" alt="pendant" referrerpolicy="no-referrer" />
        <img :src="userInfo.face" alt="avatar" referrerpolicy="no-referrer" />
      </div>
      <h2 class="name">{{ userInfo.name }}</h2>
      <p class="notice">{{ userInfo.notice }}</p>
    </div>

    <div class="profile-meta">
      <div class="follower" :title="followerExact">
        <span class="follower-label">粉丝</span>
        <span class="follower-count">{{ formatFollower(userInfo.follower) }}</span>
      </div>

      <div class="meta-note">
        <div class="note-row">
          <span class="note-label">生日</span>
          <span class="note-value">4月23日</span>
        </div>
        <div class="note-row">
          <span class="note-label">周年日</span>
          <span class="note-value">6月13日</span>
        </div>
        <div class="note-row">
          <span class="note-label">粉丝群</span>
          <span class="note-value">673929083</span>
        </div>
      </div>
    </div>

    <div class="platform-links">
      <a
        class="platform-link"
        href="https://space.bilibili.com/335573347"
        target="_blank"
        rel="noopener noreferrer"
        title="打开 B 站空间"
      >
        <img :src="spaceLogo" alt="B站空间" />
        <span class="platform-id">335573347</span>
      </a>
      <a
        class="platform-link"
        href="https://live.bilibili.com/23351494"
        target="_blank"
        rel="noopener noreferrer"
        title="打开直播间"
      >
        <img :src="liveLogo" alt="直播间" />
        <span class="platform-id">23351494</span>
      </a>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import axios from 'axios'
import apiConfig from '../api/api.json'
import defaultAvatar from '../assets/images/img.png'
import spaceLogo from '../assets/images/bilibili-space-logo.svg'
import liveLogo from '../assets/images/bilibili-live-logo.png'
import config from '../config'

interface UserInfo {
  name: string
  face: string
  notice: string
  pendant: string
  follower: number | null
}

const defaultUserInfo: UserInfo = {
  name: '一杯吃吃',
  face: defaultAvatar,
  notice: 'Loading...',
  pendant: '',
  follower: null
}

const formatFollower = (count: number | null) => {
  if (count === null || count === undefined) return '--'
  if (count >= 10000) {
    const wan = count / 10000
    const text = wan >= 100 ? String(Math.round(wan)) : wan.toFixed(1).replace(/\.0$/, '')
    return `${text}万`
  }
  return count.toLocaleString('en-US')
}

const formatFollowerExact = (count: number | null) => {
  if (count === null || count === undefined) return ''
  return count.toLocaleString('en-US')
}

export default defineComponent({
  name: 'ProfilePanel',
  setup() {
    const userInfo = ref<UserInfo>(defaultUserInfo)
    const followerExact = computed(() => formatFollowerExact(userInfo.value.follower))

    const fetchUserData = async () => {
      try {
        const { url, params } = apiConfig.bilibili.userCard
        const { url: apiUrl, params: apiParams } = config.getApiUrl(url, params)
        const response = await axios.get(apiUrl, {
          params: apiParams,
          withCredentials: false
        })

        if (response.data.code === 0) {
          const userData = response.data.data
          userInfo.value = {
            name: userData.card.name || defaultUserInfo.name,
            face: userData.card.face || defaultUserInfo.face,
            notice: userData.card.sign || defaultUserInfo.notice,
            pendant: userData.card.pendant?.image || '',
            follower: typeof userData.follower === 'number' ? userData.follower : null
          }
        } else {
          userInfo.value = defaultUserInfo
        }
      } catch {
        userInfo.value = defaultUserInfo
      }
    }

    onMounted(() => {
      fetchUserData()
    })

    const handleAvatarClick = () => {
      const avatar = document.querySelector('.avatar') as HTMLElement
      if (avatar) {
        avatar.style.animation = 'none'
        avatar.offsetHeight
        avatar.style.animation = 'avatarPop 0.5s ease'
      }
      setTimeout(() => {
        window.open('https://space.bilibili.com/335573347', '_blank')
      }, 0)
    }

    return {
      userInfo,
      formatFollower,
      followerExact,
      handleAvatarClick,
      spaceLogo,
      liveLogo
    }
  }
})
</script>

<style lang="scss" scoped>
.profile-panel {
  --ink: #243556;
  --ink-soft: rgba(36, 53, 86, 0.58);
  --ink-mute: rgba(36, 53, 86, 0.42);
  --line: rgba(72, 118, 185, 0.16);

  background-color: rgba(139, 181, 241, 0.32);
  border-radius: 14px;
  padding: 2.15rem 1.5rem 1.85rem;
  box-shadow:
    0 1px 2px rgba(45, 90, 168, 0.06),
    0 10px 28px rgba(45, 90, 168, 0.1);
  text-align: center;
  width: 100%;
  max-width: 280px;
  position: relative;
  backdrop-filter: blur(12px);

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: -28px;
    width: 0;
    height: 0;
    border-left: 18px solid transparent;
    border-right: 18px solid transparent;
    border-bottom: 28px solid rgba(139, 181, 241, 0.48);
    z-index: 1;
  }

  &::before {
    left: 42px;
  }

  &::after {
    right: 42px;
  }
}

.profile-header {
  .avatar {
    width: 88px;
    height: 88px;
    margin: 0 auto 1.25rem;
    position: relative;
    cursor: pointer;
    transition: transform 0.25s ease;

    .pendant {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 140px;
      height: 140px;
      z-index: 3;
      pointer-events: none;
    }

    img:not(.pendant) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 50%;
      position: relative;
      z-index: 2;
      box-shadow: 0 6px 16px rgba(36, 53, 86, 0.18);
      transition: transform 0.25s ease, box-shadow 0.25s ease;
    }

    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      width: 34px;
      height: 34px;
      background: rgba(255, 255, 255, 0.92);
      z-index: 1;
      display: v-bind('!userInfo.pendant ? "block" : "none"');
    }

    &::before {
      left: -8px;
      clip-path: polygon(0% 90%, 29% 0%, 90% 60%);
      transform: rotate(-30deg);
    }

    &::after {
      right: -8px;
      clip-path: polygon(100% 90%, 71% 0%, 10% 60%);
      transform: rotate(30deg);
    }

    &:hover {
      transform: translateY(-1px);

      img:not(.pendant) {
        box-shadow: 0 8px 20px rgba(36, 53, 86, 0.22);
      }
    }
  }

  .name {
    margin: 0 0 0.85rem;
    font-size: 1.28rem;
    font-weight: 600;
    line-height: 1.35;
    letter-spacing: 0.04em;
    color: var(--ink);
    font-family: "Microsoft YaHei", "微软雅黑", "PingFang SC", sans-serif;
  }

  .notice {
    margin: 0;
    padding: 0;
    font-size: 0.75rem;
    line-height: 1.85;
    color: var(--ink-soft);
    font-weight: 400;
    text-align: left;
  }
}

// 主数字 + 轻资料注脚，不用表格线
.profile-meta {
  margin-top: 1.7rem;
  padding-top: 1.4rem;
  border-top: 1px solid var(--line);
  text-align: left;
}

.follower {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.2rem;
  cursor: default;

  .follower-label {
    font-size: 0.78rem;
    font-weight: 500;
    color: var(--ink-soft);
    letter-spacing: 0.06em;
  }

  .follower-count {
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--ink);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
    line-height: 1.2;
  }
}

.meta-note {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}

.note-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
}

.note-label {
  flex-shrink: 0;
  font-size: 0.68rem;
  color: var(--ink-mute);
  letter-spacing: 0.04em;
}

.note-value {
  font-size: 0.76rem;
  font-weight: 400;
  color: rgba(36, 53, 86, 0.72);
  font-variant-numeric: tabular-nums;
  text-align: right;
  line-height: 1.3;
}

.platform-links {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  margin-top: 1.55rem;
  padding-top: 1.35rem;
  border-top: 1px solid var(--line);
}

.platform-link {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  padding: 0.15rem 0;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.2s ease, transform 0.2s ease;

  img {
    height: 1.65rem;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    opacity: 0.95;
  }

  .platform-id {
    font-size: 0.68rem;
    font-weight: 500;
    color: var(--ink-mute);
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.01em;
    transition: color 0.2s ease;
  }

  &:hover {
    opacity: 0.85;
    transform: translateY(-1px);

    .platform-id {
      color: var(--ink-soft);
    }
  }
}

@keyframes avatarPop {
  0% { transform: scale(1); }
  50% { transform: scale(1.08); }
  100% { transform: scale(1); }
}
</style>
